import { createSlice, createSelector } from '@reduxjs/toolkit';
import { config } from '../constants/config';
import {
  createEntityThunks,
  createSignedUrlThunk,
  createFileUploadThunk,
  createEnsureSelectionsLoadedThunk
} from './apiEngine/entityThunkSlice';

// Create entity thunks for literary groups
const litGroupThunks = createEntityThunks({
  entityType: 'litGroup',
  basePrefix: 'newBookCreator',
  endpoints: {
    fetchAll: config.GET_API_DIRECT_LIT_GROUPS,
    fetchOne: config.GET_API_DIRECT_LIT_GROUP_PROFILE.replace(':litGroupUuid', ':id'),
    create: config.ADD_API_LIT_GROUP_AND_BOOKS,
    update: config.ADD_API_UPDATE_LIT_GROUP,
    delete: config.DELETE_API_LIT_GROUP
  }
});

// Create entity thunks for books
const bookThunks = createEntityThunks({
  entityType: 'book',
  basePrefix: 'newBookCreator',
  endpoints: {
    fetchAll: (litGroupId) => config.GET_API_DIRECT_BOOKS_BY_LIT_GROUP.replace(':litGroupUuid', litGroupId),
    fetchOne: config.GET_API_BOOK_PROFILE.replace(':bookUuid', ':id'),
    create: config.ADD_API_LIT_GROUP_BOOK,
    update: config.SAVE_API_BOOK_PROFILE.replace(':bookUuid', ':id'),
    delete: null // Assuming no delete endpoint for books
  }
});

// Create entity thunks for characters
const characterThunks = createEntityThunks({
  entityType: 'character',
  basePrefix: 'newBookCreator',
  endpoints: {
    fetchAll: (bookId) => config.GET_API_DIRECT_CHARACTERS_BY_BOOK.replace(':bookUuid', bookId),
    fetchOne: (characterId) => {
      // This function will be called with the character ID
      // We need to get the book ID from the Redux store
      // Since we can't access the store directly in this definition,
      // we'll use a placeholder that will be replaced in the fetchCharacter thunk
      return config.GET_API_DIRECT_CHARACTER_BY_BOOK
        .replace(':bookUuid', ':bookId')
        .replace(':characterUuid', characterId);
    },
    create: config.POST_API_ADD_CHAR_TO_BOOK, // Using the correctly named constant
    // update: (characterId) => `/api/direct/books/:bookId/characters/${characterId}`, // Updated to use direct fetch endpoint
    update: (characterId) => config.PUT_API_DIRECT_CHARACTER_BY_BOOK.replace(':characterUuid', characterId), // Updated to use direct fetch endpoint

    delete: null // Assuming no delete endpoint for characters
  }
});

// Create entity thunks for variations
const variationThunks = createEntityThunks({
  entityType: 'variation',
  basePrefix: 'newBookCreator',
  endpoints: {
    fetchAll: (characterId) => config.GET_API_DIRECT_VARIATIONS_BY_CHARACTER.replace(':characterUuid', characterId),
    fetchOne: `${config.BASE_URL}/variations/:id`, // May need to be added to API
    create: config.ADD_VARIATION_TO_BOOK_CHARACTER,
    update: `${config.BASE_URL}/variations/:id`, // May need to be added to API
    delete: `${config.BASE_URL}/variations/:id` // May need to be added to API
  }
});

// Create signed URL thunk
const getSignedS3Url = createSignedUrlThunk({
  basePrefix: 'newBookCreator',
  endpoint: config.API_GET_SIGNED_URL_BY_RAW_PATH
});

// Create file upload thunk for variation audio
const uploadVariationAudio = createFileUploadThunk({
  basePrefix: 'newBookCreator',
  endpoint: (metadata) => `${config.BASE_URL}/variations/${metadata.variationId}/audio`
});

// Initial state
const initialState = {
  // Literary Groups
  litGroups: [],
  litGroupStatus: 'idle',
  litGroupError: null,
  selectedLitGroup: null,

  // Books
  books: [],
  bookStatus: 'idle',
  bookError: null,
  selectedBook: null,

  // Characters
  characters: [],
  characterStatus: 'idle',
  characterError: null,
  selectedCharacter: null,

  // Variations - Normalized structure
  variations: {
    byId: {},
    byCharacter: {},
    loadingStates: {},
    operationStatus: {
      create: 'idle',
      update: 'idle',
      delete: 'idle',
      upload: 'idle'
    },
    operationErrors: {
      create: null,
      update: null,
      delete: null,
      upload: null
    }
  },
  selectedVariation: null,

  // Selection state
  selectionId: {
    litGroup: null,
    book: null,
    character: null,
    variation: null
  },
  persistSelections: true,
  selectionsInitialized: false,

  // Cache for variations
  cache: {},

  // Signed URLs
  signedUrls: {},
  signedUrlStatus: 'idle',

  // General status
  status: 'idle',
  error: null
};

// Create the slice
const newBookCreatorSlice = createSlice({
  name: 'newBookCreator',
  initialState,
  reducers: {
    // Selection reducers
    setSelection: (state, action) => {
      const { level, id, data } = action.payload;
      state.selectionId[level] = id;

      // If data is provided, update the appropriate selected entity
      if (data) {
        switch (level) {
          case 'litGroup':
            state.selectedLitGroup = { ...state.selectedLitGroup, ...data };
            break;
          case 'book':
            state.selectedBook = { ...state.selectedBook, ...data };
            break;
          case 'character':
            state.selectedCharacter = { ...state.selectedCharacter, ...data };
            break;
          case 'variation':
            state.selectedVariation = { ...state.selectedVariation, ...data };
            break;
          default:
            break;
        }
      }
    },
    clearSelection: (state, action) => {
      const level = action.payload;
      if (level) {
        state.selectionId[level] = null;

        // Clear the selected entity
        switch (level) {
          case 'litGroup':
            state.selectedLitGroup = null;
            // Also clear dependent selections
            state.selectionId.book = null;
            state.selectedBook = null;
            state.selectionId.character = null;
            state.selectedCharacter = null;
            state.selectionId.variation = null;
            state.selectedVariation = null;
            break;
          case 'book':
            state.selectedBook = null;
            // Also clear dependent selections
            state.selectionId.character = null;
            state.selectedCharacter = null;
            state.selectionId.variation = null;
            state.selectedVariation = null;
            break;
          case 'character':
            state.selectedCharacter = null;
            // Also clear dependent selections
            state.selectionId.variation = null;
            state.selectedVariation = null;
            break;
          case 'variation':
            state.selectedVariation = null;
            break;
          default:
            break;
        }
      } else {
        // Clear all selections
        state.selectionId = {
          litGroup: null,
          book: null,
          character: null,
          variation: null
        };
        state.selectedLitGroup = null;
        state.selectedBook = null;
        state.selectedCharacter = null;
        state.selectedVariation = null;
      }
    },
    setPersistSelections: (state, action) => {
      state.persistSelections = action.payload;
    },

    // Variation reducers
    clearVariationErrors: (state) => {
      state.variations.operationErrors = {
        create: null,
        update: null,
        delete: null,
        upload: null
      };
    },

    // Optimistic update reducers
    addVariationOptimistic: (state, action) => {
      const { variation, characterId } = action.payload;
      state.variations.byId[variation.uuid] = variation;
      if (!state.variations.byCharacter[characterId]) {
        state.variations.byCharacter[characterId] = [];
      }
      state.variations.byCharacter[characterId].push(variation.uuid);
    },
    updateVariationOptimistic: (state, action) => {
      const { variationId, updates } = action.payload;
      if (state.variations.byId[variationId]) {
        state.variations.byId[variationId] = {
          ...state.variations.byId[variationId],
          ...updates,
          updatedAt: new Date().toISOString()
        };
      }
    },
    deleteVariationOptimistic: (state, action) => {
      const { variationId, characterId } = action.payload;
      delete state.variations.byId[variationId];
      if (state.variations.byCharacter[characterId]) {
        state.variations.byCharacter[characterId] =
          state.variations.byCharacter[characterId].filter(id => id !== variationId);
      }
    },

    // Cache reducers
    updateCache: (state, action) => {
      const { key, data } = action.payload;
      state.cache[key] = data;
    },
    clearCache: (state) => {
      state.cache = {};
    },
  },
  extraReducers: (builder) => {
    // Custom action to clear selected character
    builder.addCase('newBookCreator/character/clearSelected', (state) => {
      state.selectedCharacter = null;
      console.log('Cleared selected character data');
    });

    // Literary Group reducers
    builder
      .addCase(litGroupThunks.fetchAll.pending, (state) => {
        state.litGroupStatus = 'loading';
      })
      .addCase(litGroupThunks.fetchAll.fulfilled, (state, action) => {
        state.litGroupStatus = 'succeeded';
        state.litGroups = action.payload.litGroups;
        state.litGroupError = null;
      })
      .addCase(litGroupThunks.fetchAll.rejected, (state, action) => {
        state.litGroupStatus = 'failed';
        state.litGroupError = action.payload || 'Failed to fetch literary groups';
      })

      .addCase(litGroupThunks.fetchOne.pending, (state) => {
        state.litGroupStatus = 'loading';
      })
      .addCase(litGroupThunks.fetchOne.fulfilled, (state, action) => {
        state.litGroupStatus = 'succeeded';
        state.selectedLitGroup = action.payload.profile;
        state.litGroupError = null;
      })
      .addCase(litGroupThunks.fetchOne.rejected, (state, action) => {
        state.litGroupStatus = 'failed';
        state.litGroupError = action.payload || 'Failed to fetch literary group';
      })

      // Book reducers
      .addCase(bookThunks.fetchAll.pending, (state) => {
        state.bookStatus = 'loading';
      })
      .addCase(bookThunks.fetchAll.fulfilled, (state, action) => {
        state.bookStatus = 'succeeded';
        state.books = action.payload.books;
        state.bookError = null;
      })
      .addCase(bookThunks.fetchAll.rejected, (state, action) => {
        state.bookStatus = 'failed';
        state.bookError = action.payload || 'Failed to fetch books';
      })

      .addCase(bookThunks.fetchOne.pending, (state) => {
        state.bookStatus = 'loading';
      })
      .addCase(bookThunks.fetchOne.fulfilled, (state, action) => {
        state.bookStatus = 'succeeded';
        console.log('BOOK PROFILE', action.payload.profile);
        state.selectedBook = action.payload.profile;
        state.bookError = null;
      })
      .addCase(bookThunks.fetchOne.rejected, (state, action) => {
        state.bookStatus = 'failed';
        state.bookError = action.payload || 'Failed to fetch book';
      })

      // Character reducers
      .addCase(characterThunks.fetchAll.pending, (state) => {
        state.characterStatus = 'loading';
      })
      .addCase(characterThunks.fetchAll.fulfilled, (state, action) => {
        state.characterStatus = 'succeeded';
        state.characters = action.payload.characters;
        state.characterError = null;
      })
      .addCase(characterThunks.fetchAll.rejected, (state, action) => {
        state.characterStatus = 'failed';
        state.characterError = action.payload || 'Failed to fetch characters';
      })

      .addCase(characterThunks.fetchOne.pending, (state) => {
        state.characterStatus = 'loading';
      })
      .addCase(characterThunks.fetchOne.fulfilled, (state, action) => {
        state.characterStatus = 'succeeded';
        console.log("CHARACTER FETCHONE", action.payload);
        state.selectedCharacter = action.payload;
        state.characterError = null;
      })
      .addCase(characterThunks.fetchOne.rejected, (state, action) => {
        state.characterStatus = 'failed';
        state.characterError = action.payload || 'Failed to fetch character';
      })

      // Variation reducers
      .addCase(variationThunks.fetchAll.pending, (state, action) => {
        const characterId = action.meta.arg;
        state.variations.loadingStates[characterId] = 'loading';
      })
      .addCase(variationThunks.fetchAll.fulfilled, (state, action) => {
        const characterId = action.meta.arg;
        const variations = action.payload.variations;

        state.variations.loadingStates[characterId] = 'succeeded';
        state.variations.byCharacter[characterId] = [];

        variations.forEach(variation => {
          const variationId = variation.uuid;
          state.variations.byId[variationId] = variation;

          if (!state.variations.byCharacter[characterId].includes(variationId)) {
            state.variations.byCharacter[characterId].push(variationId);
          }
        });
      })
      .addCase(variationThunks.fetchAll.rejected, (state, action) => {
        const characterId = action.meta.arg;
        state.variations.loadingStates[characterId] = 'failed';
      })

      // Signed URL reducers
      .addCase(getSignedS3Url.pending, (state) => {
        state.signedUrlStatus = 'loading';
      })
      .addCase(getSignedS3Url.fulfilled, (state, action) => {
        state.signedUrlStatus = 'succeeded';
        const key = `${action.payload.originalUrl}_${action.payload.userId}`;
        state.signedUrls[key] = action.payload.signedUrl;
      })
      .addCase(getSignedS3Url.rejected, (state, action) => {
        state.signedUrlStatus = 'failed';
        state.error = action.payload || 'Failed to get signed URL';
      });
  }
});

// Create ensure selections loaded thunk
const ensureSelectionsLoaded = createEnsureSelectionsLoadedThunk({
  basePrefix: 'newBookCreator',
  getSelectionId: (state) => state.user.newBookCreator.selectionId,
  getSelectedEntity: (state) => ({
    litGroup: state.user.newBookCreator.selectedLitGroup,
    book: state.user.newBookCreator.selectedBook,
    character: state.user.newBookCreator.selectedCharacter,
    variation: state.user.newBookCreator.selectedVariation
  }),
  fetchEntity: (id) => {
    if (id.litGroup) return litGroupThunks.fetchOne(id.litGroup);
    if (id.book) return bookThunks.fetchOne(id.book);
    if (id.character) return characterThunks.fetchOne(id.character);
    if (id.variation) return variationThunks.fetchOne(id.variation);
    return null;
  },
  clearSelection: (level) => newBookCreatorSlice.actions.clearSelection(level)
});

// Export actions
export const {
  setSelection,
  clearSelection,
  setPersistSelections,
  clearVariationErrors,
  addVariationOptimistic,
  updateVariationOptimistic,
  deleteVariationOptimistic,
  updateCache,
  clearCache
} = newBookCreatorSlice.actions;

// Export thunks
export const {
  fetchAll: fetchLitGroups,
  fetchOne: fetchLitGroup,
  create: createLitGroup,
  update: updateLitGroup
} = litGroupThunks;

export const {
  fetchAll: fetchBooks,
  fetchOne: fetchBook,
  create: createBook,
  update: updateBook
} = bookThunks;

// Custom fetchCharacter thunk that includes error handling and book ID
export const fetchCharacter = (characterId, optionalBookId = null, options = {}) => async (dispatch, getState) => {
  try {
    // Use the provided bookId if available, otherwise get it from the store
    let bookId = optionalBookId;

    if (!bookId) {
      const state = getState();
      bookId = state.user.newBookCreator.selectionId.book;
    }

    if (!bookId) {
      console.error('Cannot fetch character: No book selected and no bookId provided');
      return dispatch({
        type: 'newBookCreator/character/fetchOne/rejected',
        payload: 'No book selected and no bookId provided',
        error: true
      });
    }

    // Replace the :bookId placeholder with the actual book ID
    // Make sure we don't duplicate the base URL
    const path = config.GET_API_DIRECT_CHARACTER_BY_BOOK
      .replace(':bookUuid', bookId)
      .replace(':characterUuid', characterId);

    // If the path already starts with the base URL, use it directly
    // Otherwise, prepend the base URL
    const endpoint = path.startsWith(config.BASE_URL)
      ? path
      : `${config.BASE_URL}${path}`;

    // Add cache busting if requested
    const cacheBust = options.cacheBust ? `?cacheBust=${options.cacheBust}` : '';
    const finalEndpoint = `${endpoint}${cacheBust}`;

    console.log('Character fetch endpoint:', finalEndpoint, 'Path:', path, 'BASE_URL:', config.BASE_URL, 'Cache bust:', options.cacheBust || 'none');

    // Get the auth token
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      console.error('Cannot fetch character: No authentication token');
      return dispatch({
        type: 'newBookCreator/character/fetchOne/rejected',
        payload: 'No authentication token',
        error: true
      });
    }

    // Dispatch a custom action to start the fetch
    dispatch({
      type: 'newBookCreator/character/fetchOne/pending',
      meta: { arg: characterId }
    });

    // Make the API call with authentication
    const response = await fetch(finalEndpoint, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || `Error ${response.status}: ${response.statusText}`;
      } catch (e) {
        errorMessage = `Error ${response.status}: ${response.statusText}`;
      }

      console.error('Error fetching character:', errorMessage);

      return dispatch({
        type: 'newBookCreator/character/fetchOne/rejected',
        payload: errorMessage,
        error: true
      });
    }

    const data = await response.json();

    // Add detailed logging for debugging the description issue
    console.log('API RESPONSE - CHARACTER DATA:', {
      characterId,
      bookId,
      endpoint,
      rawData: data,
      description: data.description,
      name: data.name,
      avatarUrl: data.avatarUrl,
      avatarUrlType: typeof data.avatarUrl,
      isAvatarUrlBlob: data.avatarUrl?.startsWith('blob:') || false,
      isAvatarUrlHttp: data.avatarUrl?.startsWith('http') || false,
      isAvatarUrlHttps: data.avatarUrl?.startsWith('https') || false,
      isAvatarUrlLocal: data.avatarUrl?.startsWith('/') || false,
      isAvatarUrlS3: data.avatarUrl?.includes('amazonaws.com') ||
        data.avatarUrl?.includes('aai-api') ||
        data.avatarUrl?.includes('aai-users/') || false,
      isAvatarUrlSigned: data.avatarUrl?.includes('X-Amz-Signature=') || false,
      allFields: Object.keys(data)
    });

    // Dispatch the success action
    return dispatch({
      type: 'newBookCreator/character/fetchOne/fulfilled',
      payload: data
    });
  } catch (error) {
    console.error('Error fetching character:', error);

    // Don't rethrow the error to prevent infinite loop
    return dispatch({
      type: 'newBookCreator/character/fetchOne/rejected',
      payload: error.message || 'Unknown error',
      error: true
    });
  }
};

// Custom updateCharacter thunk that includes error handling and book ID
export const updateCharacter = ({ id, data }) => async (dispatch, getState) => {
  try {
    // Get the book ID from the store
    const state = getState();
    const bookId = state.user.newBookCreator.selectionId.book;

    if (!bookId) {
      console.error('Cannot update character: No book selected');
      return dispatch({
        type: 'newBookCreator/updatecharacter/rejected',
        payload: 'No book selected',
        error: true
      });
    }

    // Replace the :bookId placeholder with the actual book ID
    // const endpoint = `/api/direct/books/${bookId}/characters/${id}`;
    // Replace the :bookId placeholder with the actual book ID
    const endpoint = config.PUT_API_DIRECT_CHARACTER_BY_BOOK
      .replace(':bookUuid', bookId)
      .replace(':characterUuid', id);

    console.log('Character update endpoint:', endpoint);

    console.log('Character update endpoint:', endpoint);

    // Get the auth token
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      console.error('Cannot update character: No authentication token');
      return dispatch({
        type: 'newBookCreator/updatecharacter/rejected',
        payload: 'No authentication token',
        error: true
      });
    }

    // Dispatch a custom action to start the update
    dispatch({
      type: 'newBookCreator/updatecharacter/pending',
      meta: { arg: id }
    });

    // Log the data being sent to the API
    console.log('UPDATE CHARACTER - DATA BEING SENT:', {
      id,
      data,
      avatarUrl: data.avatarUrl,
      avatarUrlType: typeof data.avatarUrl,
      isAvatarUrlBlob: data.avatarUrl?.startsWith('blob:') || false,
      isAvatarUrlHttp: data.avatarUrl?.startsWith('http') || false,
      isAvatarUrlHttps: data.avatarUrl?.startsWith('https') || false,
      isAvatarUrlLocal: data.avatarUrl?.startsWith('/') || false,
      isAvatarUrlS3: data.avatarUrl?.includes('amazonaws.com') ||
        data.avatarUrl?.includes('aai-api') ||
        data.avatarUrl?.includes('aai-users/') || false,
      isAvatarUrlSigned: data.avatarUrl?.includes('X-Amz-Signature=') || false,
      endpoint
    });

    // Check if the avatarUrl is a blob URL and fix it if needed
    if (data.avatarUrl && data.avatarUrl.startsWith('blob:')) {
      console.log('WARNING: Attempting to save blob URL to database. Replacing with default avatar URL.');
      data = {
        ...data,
        avatarUrl: '/narrator_1-1-480x490.png' // Use a default avatar URL
      };
    }

    // Make the API call with authentication
    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || `Error ${response.status}: ${response.statusText}`;
      } catch (e) {
        errorMessage = `Error ${response.status}: ${response.statusText}`;
      }

      console.error('Error updating character:', errorMessage);

      return dispatch({
        type: 'newBookCreator/updatecharacter/rejected',
        payload: errorMessage,
        error: true
      });
    }

    const updatedData = await response.json();

    // Log the response data
    console.log('UPDATE CHARACTER - RESPONSE DATA:', {
      updatedData,
      avatarUrl: updatedData.avatarUrl,
      avatarUrlType: typeof updatedData.avatarUrl,
      isAvatarUrlBlob: updatedData.avatarUrl?.startsWith('blob:') || false,
      isAvatarUrlHttp: updatedData.avatarUrl?.startsWith('http') || false,
      isAvatarUrlHttps: updatedData.avatarUrl?.startsWith('https') || false,
      isAvatarUrlLocal: updatedData.avatarUrl?.startsWith('/') || false,
      isAvatarUrlS3: updatedData.avatarUrl?.includes('amazonaws.com') ||
        updatedData.avatarUrl?.includes('aai-api') ||
        updatedData.avatarUrl?.includes('aai-users/') || false,
      isAvatarUrlSigned: updatedData.avatarUrl?.includes('X-Amz-Signature=') || false
    });

    // Dispatch the success action
    return dispatch({
      type: 'newBookCreator/updatecharacter/fulfilled',
      payload: updatedData
    });
  } catch (error) {
    console.error('Error updating character:', error);

    return dispatch({
      type: 'newBookCreator/updatecharacter/rejected',
      payload: error.message || 'Unknown error',
      error: true
    });
  }
};

export const {
  fetchAll: fetchCharacters,
  create: createCharacter
} = characterThunks;

export const {
  fetchAll: fetchVariations,
  fetchOne: fetchVariation,
  create: createVariation,
  update: updateVariation,
  remove: deleteVariation
} = variationThunks;

export { getSignedS3Url, uploadVariationAudio, ensureSelectionsLoaded };

// Export selectors
export const selectLitGroups = state => state.user.newBookCreator.litGroups;
export const selectLitGroupsStatus = state => state.user.newBookCreator.litGroupStatus;
export const selectLitGroupsError = state => state.user.newBookCreator.litGroupError;

export const selectBooks = state => state.user.newBookCreator.books;
export const selectBooksStatus = state => state.user.newBookCreator.bookStatus;
export const selectBooksError = state => state.user.newBookCreator.bookError;

export const selectCharacters = state => state.user.newBookCreator.characters;
export const selectCharactersStatus = state => state.user.newBookCreator.characterStatus;
export const selectCharactersError = state => state.user.newBookCreator.characterError;

export const selectSelectionId = (state, level) => state.user.newBookCreator.selectionId[level];
export const selectPersistSelections = state => state.user.newBookCreator.persistSelections;

export const selectSelectedLitGroup = state => state.user.newBookCreator.selectedLitGroup;
export const selectSelectedBook = state => state.user.newBookCreator.selectedBook;
export const selectSelectedCharacter = state => state.user.newBookCreator.selectedCharacter;
export const selectSelectedVariation = state => state.user.newBookCreator.selectedVariation;

// Memoized selector for variations by character
export const selectVariationsByCharacter = characterId => createSelector(
  [
    state => state.user.newBookCreator.variations.byId,
    state => state.user.newBookCreator.variations.byCharacter[characterId] || []
  ],
  (byId, variationIds) => variationIds.map(id => byId[id]).filter(Boolean)
);

export const selectVariationsLoadingState = characterId => createSelector(
  [state => state.user.newBookCreator.variations.loadingStates],
  loadingStates => loadingStates[characterId] || 'idle'
);

export const selectVariationOperationStatus = operation => createSelector(
  [state => state.user.newBookCreator.variations.operationStatus],
  operationStatus => operationStatus[operation]
);

export const selectVariationOperationError = operation => createSelector(
  [state => state.user.newBookCreator.variations.operationErrors],
  operationErrors => operationErrors[operation]
);

export const selectSignedUrl = (state, originalUrl, userId) => {
  if (!originalUrl || !userId) return null;
  const key = `${originalUrl}_${userId}`;
  return state.user.newBookCreator.signedUrls[key] || null;
};

export default newBookCreatorSlice.reducer;
